import { useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useTheme } from '@/contexts/ThemeContext';
import { Sun, Moon, LogOut, Menu, KeyRound, Eye, EyeOff, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { toast } from 'sonner';

// 'superadmin' is a key, not a name
const ROLE_LABEL = { superadmin: 'Super Admin', admin: 'Admin', manager: 'Manager', viewer: 'Viewer' };

function getPasswordStrength(pw) {
  if (!pw) return { score: 0, label: '', color: '' };
  let score = 0;
  if (pw.length >= 8) score++;
  if (pw.length >= 12) score++;
  if (/[A-Z]/.test(pw)) score++;
  if (/[a-z]/.test(pw)) score++;
  if (/[0-9]/.test(pw)) score++;
  if (/[^A-Za-z0-9]/.test(pw)) score++;
  if (score <= 2) return { score: Math.round(score / 6 * 100), label: 'Weak', color: 'bg-red-500' };
  if (score <= 3) return { score: Math.round(score / 6 * 100), label: 'Fair', color: 'bg-amber-500' };
  if (score <= 4) return { score: Math.round(score / 6 * 100), label: 'Good', color: 'bg-blue-500' };
  return { score: Math.round(score / 6 * 100), label: 'Strong', color: 'bg-green-500' };
}

function isPasswordValid(pw) {
  return pw && pw.length >= 8 && /[A-Z]/.test(pw) && /[a-z]/.test(pw) && /[0-9]/.test(pw);
}

export default function Header({ onMenuToggle }) {
  const { user, logout, api } = useAuth();
  const { theme, toggle } = useTheme();
  const [cpOpen, setCpOpen] = useState(false);
  const [cpForm, setCpForm] = useState({ current: '', newPw: '', confirm: '' });
  const [cpLoading, setCpLoading] = useState(false);
  const [cpError, setCpError] = useState('');
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNew, setShowNew] = useState(false);

  const handleChangePassword = async () => {
    setCpError('');
    if (!cpForm.current) { setCpError('Enter current password'); return; }
    if (!isPasswordValid(cpForm.newPw)) { setCpError('New password must be 8+ chars with uppercase, lowercase, and digit'); return; }
    if (cpForm.newPw !== cpForm.confirm) { setCpError('Passwords do not match'); return; }
    setCpLoading(true);
    try {
      await api.post('/auth/change-password', { current_password: cpForm.current, new_password: cpForm.newPw });
      toast.success('Password changed successfully');
      setCpOpen(false);
      setCpForm({ current: '', newPw: '', confirm: '' });
    } catch (err) {
      setCpError(err.response?.data?.detail || 'Failed to change password');
    } finally { setCpLoading(false); }
  };

  const pwStrength = getPasswordStrength(cpForm.newPw);
  const passwordsMatch = cpForm.confirm && cpForm.newPw === cpForm.confirm;
  const passwordsMismatch = cpForm.confirm && cpForm.newPw !== cpForm.confirm;

  return (
    <>
      <header className="h-16 bg-card/80 backdrop-blur-xl border-b border-border/50 flex items-center justify-between px-6 sticky top-0 z-30" data-testid="header">
        <div className="flex items-center gap-3">
          <button onClick={onMenuToggle} className="lg:hidden text-muted-foreground hover:text-foreground" data-testid="menu-toggle">
            <Menu size={20} />
          </button>
        </div>
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="icon" onClick={toggle} data-testid="theme-toggle" className="rounded-full">
            {theme === 'light' ? <Moon size={18} strokeWidth={1.5} /> : <Sun size={18} strokeWidth={1.5} />}
          </Button>
          <div className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-muted/50">
            <div className="w-7 h-7 rounded-full bg-primary/20 flex items-center justify-center text-xs font-heading font-semibold text-primary">
              {user?.name?.charAt(0)?.toUpperCase() || 'U'}
            </div>
            <div className="hidden sm:flex flex-col">
              <span className="text-sm font-medium text-foreground leading-tight">{user?.name}</span>
              <span className="text-[10px] text-muted-foreground capitalize leading-tight">{ROLE_LABEL[user?.role] || user?.role}</span>
            </div>
          </div>
          <Button variant="ghost" size="icon" onClick={() => setCpOpen(true)} data-testid="change-password-btn" className="rounded-full text-muted-foreground hover:text-foreground" title="Change password">
            <KeyRound size={16} strokeWidth={1.5} />
          </Button>
          <Button variant="ghost" size="icon" onClick={logout} data-testid="logout-btn" className="rounded-full text-muted-foreground hover:text-destructive">
            <LogOut size={18} strokeWidth={1.5} />
          </Button>
        </div>
      </header>

      {/* Change Password Dialog */}
      <Dialog open={cpOpen} onOpenChange={v => { if (!v) { setCpOpen(false); setCpForm({ current: '', newPw: '', confirm: '' }); setCpError(''); } }}>
        <DialogContent className="sm:max-w-md" data-testid="change-password-dialog" onInteractOutside={e => e.preventDefault()}>
          <DialogHeader>
            <DialogTitle className="font-heading">Change Password</DialogTitle>
            <DialogDescription>Update your account password</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            {/* Current Password */}
            <div className="space-y-2">
              <Label className="text-xs font-heading uppercase tracking-widest text-muted-foreground">Current Password</Label>
              <div className="relative">
                <Input type={showCurrent ? 'text' : 'password'} placeholder="Enter current password" value={cpForm.current}
                  onChange={e => setCpForm({...cpForm, current: e.target.value})} data-testid="cp-current-input" />
                <button type="button" onClick={() => setShowCurrent(!showCurrent)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground">
                  {showCurrent ? <EyeOff size={14} /> : <Eye size={14} />}
                </button>
              </div>
            </div>

            {/* New Password */}
            <div className="space-y-2">
              <Label className="text-xs font-heading uppercase tracking-widest text-muted-foreground">New Password</Label>
              <div className="relative">
                <Input type={showNew ? 'text' : 'password'} placeholder="Create strong password" value={cpForm.newPw}
                  onChange={e => setCpForm({...cpForm, newPw: e.target.value})} data-testid="cp-new-input" />
                <button type="button" onClick={() => setShowNew(!showNew)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground">
                  {showNew ? <EyeOff size={14} /> : <Eye size={14} />}
                </button>
              </div>
              {cpForm.newPw && (
                <div className="space-y-1">
                  <div className="flex items-center gap-2">
                    <div className="flex-1 h-1.5 bg-muted rounded-full overflow-hidden">
                      <div className={`h-full ${pwStrength.color} transition-all duration-300`} style={{ width: `${pwStrength.score}%` }} />
                    </div>
                    <span className={`text-xs font-medium ${pwStrength.score <= 33 ? 'text-red-500' : pwStrength.score <= 66 ? 'text-amber-500' : 'text-green-500'}`}>
                      {pwStrength.label}
                    </span>
                  </div>
                  <div className="flex flex-wrap gap-x-3 gap-y-0.5 text-[10px] text-muted-foreground">
                    <span className={cpForm.newPw.length >= 8 ? 'text-green-600 dark:text-green-400' : ''}>8+ chars {cpForm.newPw.length >= 8 ? '\u2713' : ''}</span>
                    <span className={/[A-Z]/.test(cpForm.newPw) ? 'text-green-600 dark:text-green-400' : ''}>Uppercase {/[A-Z]/.test(cpForm.newPw) ? '\u2713' : ''}</span>
                    <span className={/[a-z]/.test(cpForm.newPw) ? 'text-green-600 dark:text-green-400' : ''}>Lowercase {/[a-z]/.test(cpForm.newPw) ? '\u2713' : ''}</span>
                    <span className={/[0-9]/.test(cpForm.newPw) ? 'text-green-600 dark:text-green-400' : ''}>Digit {/[0-9]/.test(cpForm.newPw) ? '\u2713' : ''}</span>
                  </div>
                </div>
              )}
            </div>

            {/* Confirm Password (no eye toggle) */}
            <div className="space-y-2">
              <Label className="text-xs font-heading uppercase tracking-widest text-muted-foreground">Confirm Password</Label>
              <Input type="password" placeholder="Re-enter new password" value={cpForm.confirm}
                onChange={e => setCpForm({...cpForm, confirm: e.target.value})} data-testid="cp-confirm-input" />
              {passwordsMatch && <p className="text-[11px] text-green-600 dark:text-green-400">Passwords match</p>}
              {passwordsMismatch && <p className="text-[11px] text-red-500">Passwords do not match</p>}
            </div>

            {cpError && <p className="text-sm text-destructive font-medium" data-testid="cp-error">{cpError}</p>}
          </div>
          <DialogFooter>
            <Button variant="outline" size="sm" onClick={() => { setCpOpen(false); setCpForm({ current: '', newPw: '', confirm: '' }); setCpError(''); }}>Cancel</Button>
            <Button size="sm" onClick={handleChangePassword}
              disabled={cpLoading || !cpForm.current || !isPasswordValid(cpForm.newPw) || cpForm.newPw !== cpForm.confirm}
              data-testid="cp-save-btn">
              {cpLoading ? <Loader2 size={14} className="mr-1.5 animate-spin" /> : <KeyRound size={14} className="mr-1.5" />}
              Change Password
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
